import { useCallback, useEffect, useState } from "react";
import { Loader2, Smartphone } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { AppRequestPanel } from "@/components/AppRequestPanel";
import { LockedFeature } from "@/components/LockedFeature";
import { StoreIconUpload } from "@/components/StoreIconUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Field = ({ label, hint, count, children }) => (
  <label className="block space-y-2">
    <span className="flex items-baseline justify-between gap-3">
      <span className="font-mono text-xs uppercase tracking-widest text-gold">{label}</span>
      {count && <span className="font-mono text-[10px] text-slate-500">{count}</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

const input = "h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold";

/** Business and Enterprise operators design their own passenger app here. */
export const AppDesigner = () => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [icon, setIcon] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/company/app-design");
      setState(data);
      setForm({ ...data.resolved, ...data.design });
      setIcon(data.icon_urls?.["512"] || "");
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(""); setError("");
    try {
      const { data } = await api.put("/company/app-design", form);
      setState(data);
      setForm({ ...data.resolved, ...data.design });
      setMsg("Saved. Your app carries this the next time it is built — and on passengers' home screens right away.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!state || !form) {
    return <Loader2 data-testid="app-design-loading" className="h-5 w-5 animate-spin text-gold" />;
  }

  if (!state.white_label) {
    return (
      <LockedFeature testId="app-design-locked" title="Your own app on the stores"
        description={`You are on ${state.plan_name}. On Business or Enterprise your passenger app carries your name, your icon and your colours on Google Play and the App Store — and you design it right here.`} />
    );
  }

  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <div data-testid="app-designer" className="space-y-6">
      {msg && <p data-testid="app-design-saved" className="text-sm text-emerald-400">{msg}</p>}
      {error && <p data-testid="app-design-error" className="text-sm text-red-400">{error}</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_260px]">
        <div className="space-y-5">
          <Field label="App name" hint="Shown on the store listing. 30 characters max."
            count={`${form.app_name.length}/30`}>
            <Input data-testid="app-design-name" maxLength={30} value={form.app_name}
              onChange={(e) => set({ app_name: e.target.value })} className={input} />
          </Field>
          <Field label="Name under the icon" hint="What fits on a home screen. 12 characters max."
            count={`${form.launcher_name.length}/12`}>
            <Input data-testid="app-design-launcher" maxLength={12} value={form.launcher_name}
              onChange={(e) => set({ launcher_name: e.target.value })} className={input} />
          </Field>
          <Field label="One-line description" hint="The line under your name in search results."
            count={`${form.short_description.length}/80`}>
            <Input data-testid="app-design-short" maxLength={80} value={form.short_description}
              onChange={(e) => set({ short_description: e.target.value })} className={input} />
          </Field>
          <Field label="Store description" count={`${form.description.length}/400`}>
            <Textarea data-testid="app-design-description" rows={5} maxLength={400}
              value={form.description} onChange={(e) => set({ description: e.target.value })}
              className="border-ink-600 bg-ink-800 text-sm text-slate-100 focus-visible:ring-gold" />
          </Field>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Accent colour">
              <Input data-testid="app-design-theme" type="color" value={form.theme_color}
                onChange={(e) => set({ theme_color: e.target.value })} className={input} />
            </Field>
            <Field label="Background">
              <Input data-testid="app-design-background" type="color" value={form.background_color}
                onChange={(e) => set({ background_color: e.target.value })} className={input} />
            </Field>
            <Field label="Rotation">
              <select data-testid="app-design-orientation" value={form.orientation}
                onChange={(e) => set({ orientation: e.target.value })}
                className="h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100">
                <option value="portrait">Upright only</option>
                <option value="any">Rotates</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Preview</p>
          <div data-testid="app-design-preview"
            className="mx-auto w-[240px] overflow-hidden rounded-[2rem] border-4 border-ink-600 shadow-2xl"
            style={{ backgroundColor: form.background_color }}>
            <div className="h-6" style={{ backgroundColor: form.theme_color, opacity: 0.85 }} />
            <div className="flex flex-col items-center gap-3 px-5 py-8 text-center">
              {icon ? (
                <img src={icon} alt="App icon" className="h-16 w-16 rounded-2xl object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20">
                  <Smartphone className="h-6 w-6 text-white/60" />
                </div>
              )}
              <p className="text-sm font-medium text-white">{form.app_name || "Your app"}</p>
              <p className="text-[11px] leading-relaxed text-white/60">{form.short_description}</p>
              <span className="mt-2 rounded-full px-4 py-1.5 text-[11px] font-medium text-ink-900"
                style={{ backgroundColor: form.theme_color }}>Book a ride</span>
              <p className="mt-6 font-mono text-[9px] uppercase tracking-widest text-white/30">
                {form.launcher_name || "home screen"}
              </p>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500">
            Passengers open it at<br />
            <span className="break-all font-mono text-[10px] text-slate-400">/ride/{state.handle}</span>
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
        <StoreIconUpload testId="app-design-icon" />
      </div>

      <Button data-testid="app-design-save" disabled={busy} onClick={save}
        className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save my app"}
      </Button>
      {!state.icon_ready && (
        <p data-testid="app-design-icon-warning" className="text-xs text-amber-400">
          Upload a square icon above before your app goes to a store — Google and Apple both refuse a
          listing without one.
        </p>
      )}

      <AppRequestPanel />
    </div>
  );
};
